/**
 * ContactSearchBar — header da sidebar do chat:
 * título + botão "novo chat" + campo de busca + tabs Todas/Não lidas.
 * Wireframe original; rebrand visual DS (var(--sminy-primary)).
 */

import React from 'react';
import { Search, Plus } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext.tsx';

interface ContactSearchBarProps {
  searchTerm: string;
  onSearchChange: (s: string) => void;
  showUnreadOnly: boolean;
  onToggleUnread: (v: boolean) => void;
  unreadCount: number;
  onNewChat: () => void;
  conversationTabs?: React.ReactNode;
  filterControl?: React.ReactNode;
  filterChips?: React.ReactNode;
}

const ContactSearchBar: React.FC<ContactSearchBarProps> = ({
  searchTerm,
  onSearchChange,
  showUnreadOnly,
  onToggleUnread,
  unreadCount,
  onNewChat,
  conversationTabs,
  filterControl,
  filterChips,
}) => {
  const { isDark } = useTheme();
  return (
    <div className={`flex-shrink-0 p-4 border-b ${isDark ? 'border-gray-700 bg-gray-900 text-gray-200' : 'border-gray-200 bg-white text-gray-800'}`}>
      {/* Title + new chat */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold tracking-tight">Conversas</h2>
        <button
          onClick={onNewChat}
          className="p-2 rounded-full bg-sminy-primary text-white shadow-sm hover:bg-sminy-primary/90 hover:scale-105 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-sminy-primary/40 focus:ring-offset-2"
          title="Iniciar novo chat"
          aria-label="Iniciar novo chat"
        >
          <Plus className="h-4 w-4 lg:h-5 lg:w-5" />
        </button>
      </div>

      {/* Search */}
      <div className="flex items-center gap-2 mb-3">
      <div className="relative min-w-0 flex-1">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
        <input
          type="text"
          placeholder="Buscar contato..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          className={`w-full h-10 pl-10 pr-3 py-2 text-sm border rounded-xl placeholder-gray-400 transition-colors focus:outline-none focus:ring-2 focus:ring-sminy-primary/30 focus:border-sminy-primary ${isDark ? 'border-gray-700 bg-gray-800 text-gray-200' : 'border-gray-200 bg-gray-50 focus:bg-white'}`}
        />
      </div>
      {filterControl}

      </div>
      {filterChips && <div className="mb-3">{filterChips}</div>}

      {/* Seletor principal de conversas */}
      {conversationTabs ?? <div className="flex gap-1 p-1 rounded-xl bg-gray-100">
        <button
          onClick={() => onToggleUnread(false)}
          className={`flex-1 py-1.5 text-xs lg:text-sm font-medium rounded-lg transition-all ${
            !showUnreadOnly
              ? 'bg-white text-sminy-primary shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Todas
        </button>
        <button
          onClick={() => onToggleUnread(true)}
          className={`flex-1 py-1.5 text-xs lg:text-sm font-medium rounded-lg transition-all flex items-center justify-center gap-1.5 ${
            showUnreadOnly
              ? 'bg-white text-sminy-primary shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Não lidas
          {unreadCount > 0 && (
            <span className="inline-flex items-center justify-center h-4 min-w-4 px-1 text-[10px] font-bold text-white bg-emerald-500 rounded-full">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
      </div>}
    </div>
  );
};

export default ContactSearchBar;
